import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  Row,
  Col } from "reactstrap";
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import Heatmap from "./ku/Heatmap";
import { KU_NAMES, KU_DESCRIPTIONS, normalizeKuId } from "../../../utils/kuInfo";
import Commits from "./ku/Commits";
import Form from "./ku/Form";
import { getOrganization } from "../../../utils/Tokens";


// Wrap a long KU description into short lines so the Chart.js tooltip stays readable
const wrapText = (text, maxChars = 60) => {
    if (!text) return [];
    const lines = [];
    let current = "";
    String(text).split(/\s+/).forEach((word) => {
        if (current && (current + " " + word).length > maxChars) {
            lines.push(current);
            current = word;
        } else {
            current = current ? current + " " + word : word;
        }
    });
    if (current) lines.push(current);
    return lines;
};

// Options for the "View Organization Skills" bar chart. The tooltip shows the
// same KU info (id - name + description) as the Knowledge Units heatmap.
const organizationSkillsChartOptions = {
    responsive: true,
    layout: {
        padding: {
            right: 150,
        },
    },
    interaction: {
        mode: "index",
        intersect: false,
    },
    plugins: {
        legend: {
            position: "top",
        },
        tooltip: {
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            titleColor: "#333",
            bodyColor: "#555",
            borderColor: "#ddd",
            borderWidth: 1,
            padding: 12,
            boxPadding: 4,
            titleFont: { size: 13, weight: "bold" },
            bodyFont: { size: 12 },
            callbacks: {
                title: (items) => {
                    if (!items.length) return "";
                    const ku = items[0].label;
                    const key = normalizeKuId(ku);
                    const name = KU_NAMES[key];
                    return name ? `${ku} \u2013 ${name}` : ku;
                },
                afterBody: (items) => {
                    if (!items.length) return [];
                    const key = normalizeKuId(items[0].label);
                    const description = KU_DESCRIPTIONS[key];
                    return description ? ["", ...wrapText(description)] : [];
                },
            },
        },
    },
    scales: {
        x: {
            title: {
                display: true,
                text: "Knowledge Unit",
            },
        },
        y: {
            type: "linear",
            position: "left",
            beginAtZero: true,
            title: {
                display: true,
                text: "Number of Files",
            },
        },
        y1: {
            type: "linear",
            position: "right",
            beginAtZero: true,
            title: {
                display: true,
                text: "Number of Authors",
            },
            grid: {
                drawOnChartArea: false,
            },
        },
    },
};

function KnowleageUnits() {
    const [repos, setRepos] = useState([]);
    const [selectedRepo, setSelectedRepo] = useState('');
    const [repoUrl, setRepoUrl] = useState('');
    const [showScreen, setShowScreen] = useState(false);
    const [resultsOfAnalysis, setResultsOfAnalysis] = useState(false);

    const [commits, setCommits] = useState([]);
    const [files, setFiles] = useState([]);
    const [progress, setProgress] = useState(0);
    const [totalFiles, setTotalFiles] = useState(0);
    const [loading, setLoading] = useState(false);
    const [analysisResults, setAnalysisResults] = useState([]);
    const [commitLimit, setCommitLimit] = useState(100);
    const [chartData, setChartData] = useState(null);
    const [showChart, setShowChart] = useState(false); 
    const [organization, setOrganization] = useState('');

    const handleSelectRepo = (repoName, repoUrl) => {
        setSelectedRepo(repoName);
        setRepoUrl(repoUrl);
        setShowScreen(true);
        setResultsOfAnalysis(false); // Set resultsOfAnalysis to false when selecting a repo
    };

    const handleCloseChart = () => {
        //setShowScreen(false);
        setShowChart(false)
    };    

    const handleViewOrganizationSkills = async () => {
        try {
            const response = await fetch(process.env.REACT_APP_API_URL_KU + '/organizationskills', {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("accessTokenSkillab")}`,
                },
            });
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const analysisData = await response.json();

            // Sort the data by KU
            const sortedData = analysisData.sort((a, b) => {
                const numA = parseInt(a.ku_name.slice(1)); // Extracts number from "K1", "K10",
                const numB = parseInt(b.ku_name.slice(1));
                return numA - numB;
            });

            // Map the sorted data directly to chart labels and datasets
            const labels = sortedData.map(item => item.ku_name);
            const dataFiles = sortedData.map(item => item.total_files);
            const dataEmployees = sortedData.map(item => item.total_authors);

            // Set chart data
            setChartData({
                labels: labels,
                datasets: [
                    {
                        label: 'Number of Files',
                        data: dataFiles,
                        backgroundColor: 'rgba(75, 192, 192, 0.2)',
                        borderColor: 'rgba(75, 192, 192, 1)',
                        borderWidth: 1,
                        yAxisID: 'y',
                    },
                    {
                        label: 'Number of Authors',
                        data: dataEmployees,
                        backgroundColor: 'rgba(255, 99, 132, 0.2)',
                        borderColor: 'rgba(255, 99, 132, 1)',
                        borderWidth: 1,
                        yAxisID: 'y1',
                    },
                ],
            });
            setShowChart(true);
        }
        catch (error) {
            console.error('Failed to load analysis data:', error);
        }
    };

    

    const getRepos = async () => {
        axios
            .get(process.env.REACT_APP_API_URL_KU + "/repos?organization=" + organization, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("accessTokenSkillab")}`,
                },
            })
            .then((res) => {
                const sortedRepos = res.data.sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at)
                );
                console.log("sorted repos:", sortedRepos);
                setRepos(res.data);
            });
    };

    useEffect(() => {
        const fetchOrganization = async () => {
            const org = await getOrganization();
            setOrganization(org);
        };

        fetchOrganization();
    }, []);

    useEffect(() => {
        if (!organization) return;
        getRepos();
    }, [organization]);

    return (
        <>
            <Row>
                <Col lg="12" xl="4">
                    <Card>
                        <CardBody>
                            <div className="flex justify-start p-6">
                                <div className="w-full max-w-md">
                                    <ul style={{paddingLeft:"0px", maxHeight: "500px", overflowY: "auto" }}>
                                        {repos.map((repo) => (
                                            <li
                                                key={repo.name}
                                                style={{display:"flex", justifyContent:"space-between", alignItems:"center" }}
                                                className={`p-4 border border-gray-200 rounded-lg shadow-sm ${
                                                repo.name === selectedRepo ? 'bg-success' : 'bg-white'
                                                }`}
                                            >
                                                <span>{repo.name}</span>
                                                <button
                                                    onClick={() => handleSelectRepo(repo.name, repo.url)}
                                                    aria-label={`Select ${repo.name}`}
                                                >
                                                    <i className="fas fa-eye text-lg"></i>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg="12" xl="8">
                    <Card>
                        <CardBody>
                            <Button 
                                onClick={handleViewOrganizationSkills} 
                                color="info"
                            >
                                View Organization Skills
                            </Button>
                            {showChart && chartData && (
                                <div className="mt-8">
                                    <Bar data={chartData} options={organizationSkillsChartOptions} />
                                    <Button 
                                        color="info"
                                        onClick={handleCloseChart} 
                                        className="mt-4 px-4 py-2 bg-[#c72424] text-white rounded"
                                    >
                                        Close Chart
                                    </Button>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
                {showScreen &&
                    <Col md="12">
                        <Card>
                            <CardBody>
                                <Form
                                    commits={commits}
                                    setCommits={setCommits}
                                    setProgress={setProgress}
                                    setTotalFiles={setTotalFiles}
                                    loading={loading}
                                    setLoading={setLoading}
                                    setAnalysisResults={setAnalysisResults}
                                    initialRepoUrl={repoUrl}
                                    setResultsOfAnalysis={setResultsOfAnalysis} // Pass the setter function
                                />
                                {resultsOfAnalysis && ( // Render only if resultsOfAnalysis is true
                                    <Commits commits={commits} loading={loading} />
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                }
            </Row>
        </>
    );
}

export default KnowleageUnits;